import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, MessageSquare, Phone, Mail, Bell, Users } from "lucide-react";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";

const CHANNELS = [
  { id: "sms", label: "SMS", icon: Phone },
  { id: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { id: "email", label: "Email", icon: Mail },
  { id: "push", label: "Push", icon: Bell },
];
const AUDIENCES = [
  { id: "all", label: "All active members" },
  { id: "subscribers", label: "All subscribers" },
  { id: "basic", label: "Basic members" },
  { id: "premium", label: "Premium members" },
];

export const NotifyMembersDialog = ({ open, type, refId, defaultTitle, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [audience, setAudience] = useState("all");
  const [channels, setChannels] = useState(["push"]);
  const [messages, setMessages] = useState({ sms: "", whatsapp: "", email: "", push: { title: "", body: "", url: "" } });
  const [counts, setCounts] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setAudience("all"); setChannels(["push"]);
    Promise.all([endpoints.notifPreview(type, refId), endpoints.notifAudienceCounts()])
      .then(([p, c]) => { setMessages(p.data.messages); setCounts(c.data || {}); })
      .catch((e) => toast.error(apiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, type, refId]);

  const toggleCh = (id) => setChannels((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const send = async (force = false) => {
    if (!channels.length) return toast.error("Select at least one channel");
    setSending(true);
    try {
      await endpoints.notifSend({ type, refId, audience, channels, messages, force });
      toast.success("Notification is being sent to members");
      onClose(true);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 409) {
        if (window.confirm("These members were already notified about this. Send again anyway?")) {
          return send(true);
        }
      } else {
        toast.error(apiError(e?.response?.data?.detail));
      }
    } finally { setSending(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="notify-dialog">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !sending && onClose(false)} />
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
            className="relative w-full max-w-lg bg-[#121620] border border-[#273042] rounded-2xl p-6 max-h-[92vh] overflow-y-auto">
            <button onClick={() => !sending && onClose(false)} className="absolute right-4 top-4 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 mb-1"><Bell className="w-5 h-5 text-[#E11D2A]" /><h2 className="text-xl font-bold text-white">Notify members</h2></div>
            <p className="text-xs text-slate-500 mb-4 truncate">{defaultTitle}</p>

            {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div> : (
              <div className="space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Audience</div>
                  <div className="grid grid-cols-2 gap-2">
                    {AUDIENCES.map((a) => (
                      <button key={a.id} onClick={() => setAudience(a.id)} data-testid={`notify-audience-${a.id}`}
                        className={`h-11 rounded-lg text-sm font-semibold border text-left px-3 transition-colors ${audience === a.id ? "border-[#E11D2A] bg-[#E11D2A]/10 text-white" : "border-[#273042] bg-[#0A0C10] text-slate-300"}`}>
                        {a.label}<span className="block text-[10px] text-slate-500 font-normal">{counts[a.id] ?? 0} members</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Channels</div>
                  <div className="flex flex-wrap gap-2">
                    {CHANNELS.map((c) => {
                      const Icon = c.icon; const on = channels.includes(c.id);
                      return (
                        <button key={c.id} onClick={() => toggleCh(c.id)} data-testid={`notify-channel-${c.id}`}
                          className={`h-10 px-4 rounded-full text-sm font-semibold border flex items-center gap-2 transition-colors ${on ? "border-[#2563EB] bg-[#2563EB]/15 text-white" : "border-[#273042] bg-[#0A0C10] text-slate-300"}`}>
                          <Icon className="w-4 h-4" /> {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Message (preview & edit)</div>
                  {(channels.includes("sms") || channels.includes("whatsapp")) && (
                    <div>
                      <label className="text-[11px] text-slate-500">SMS / WhatsApp text</label>
                      <textarea value={messages.sms} data-testid="notify-msg-sms"
                        onChange={(e) => setMessages({ ...messages, sms: e.target.value, whatsapp: e.target.value })}
                        rows={3} className="w-full px-3 py-2 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm outline-none focus:border-[#E11D2A]" />
                    </div>
                  )}
                  {channels.includes("email") && (
                    <div>
                      <label className="text-[11px] text-slate-500">Email body</label>
                      <textarea value={messages.email} data-testid="notify-msg-email"
                        onChange={(e) => setMessages({ ...messages, email: e.target.value })}
                        rows={3} className="w-full px-3 py-2 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm outline-none focus:border-[#E11D2A]" />
                    </div>
                  )}
                  {channels.includes("push") && (
                    <div className="grid grid-cols-1 gap-2">
                      <input value={messages.push?.title || ""} data-testid="notify-msg-push-title" placeholder="Push title"
                        onChange={(e) => setMessages({ ...messages, push: { ...messages.push, title: e.target.value } })}
                        className="h-10 px-3 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm outline-none focus:border-[#E11D2A]" />
                      <textarea value={messages.push?.body || ""} data-testid="notify-msg-push-body" placeholder="Push message" rows={2}
                        onChange={(e) => setMessages({ ...messages, push: { ...messages.push, body: e.target.value } })}
                        className="w-full px-3 py-2 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm outline-none focus:border-[#E11D2A]" />
                    </div>
                  )}
                  <p className="text-[11px] text-slate-500">Links always open inside B&B KIGALI FM. The original YouTube URL is never sent.</p>
                </div>

                <button onClick={() => send(false)} disabled={sending} data-testid="notify-send-btn"
                  className="w-full h-12 rounded-full bg-[#E11D2A] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Send notification
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
